import React, { memo, useCallback, useMemo } from 'react';

import CouponSection2 from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/CouponSection2';
import CouponItem2 from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/CouponItem2';

import { ICoupon } from '@wconcept/wconcept-common/models/event/custom';
import {
  PromotionFcfsCouponModule as PromotionFcfsCouponModuleType,
  PromotionNotice,
} from '@wconcept/wconcept-common/models/renewal/promotion';

import useGa4Event from '@hooks/useGa4Event';
import {
  useFcfsCouponIssue,
  useFcfsCouponModuleState,
} from '@hooks/promotion/useFcfsCoupon';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';

import {
  type PromotionCustomColorsFull,
  type PromotionModuleVisualTokens,
} from '@utils/promotionContentVisual';

import {
  fcfsBoldNumericDescParts,
  fcfsCouponDesc,
  fcfsCouponItemStatus,
  fcfsFallbackEntryTitle,
  fcfsFallbackRowReactKey,
  type FcfsCouponItemPhaseOptions,
  type FcfsNoticeType,
  isCardSoldOut,
  isFcfsPublicDisplayPhase,
  phaseAllowsEntryFallback,
  resolveFcfsCouponItemDesc,
  resolveFcfsCouponItemStatus,
  type FcfsPromotionDisplayBounds,
} from '../utils/fcfsCouponSchedule';

// ---------------------------------------------------------------------------
// 타입 · CMS 색상 · JSX 헬퍼
// ---------------------------------------------------------------------------

type CouponItem2ColorType = React.ComponentProps<
  typeof CouponItem2
>['colorType'];

function fcfsCouponItemColorTypeFromFull(
  full: PromotionCustomColorsFull
): CouponItem2ColorType {
  return {
    bgColor: full.bgColor,
    pointColor: full.pointColor,
    emphasisTextColor: full.emphasisTextColor,
  };
}

/** 프리셋: white 섹션 → 검정 카드, 그 외 gray. 객체(colorType)는 그대로 전달 */
function mapPresetSectionToItemSurface(
  sectionColorType: unknown
): CouponItem2ColorType {
  if (typeof sectionColorType === 'object' && sectionColorType !== null) {
    return sectionColorType as CouponItem2ColorType;
  }
  if (sectionColorType === 'white') return 'black';
  return 'gray';
}

function sectionLiteFromFull(full: PromotionCustomColorsFull) {
  return { bgColor: full.bgColor, textColor: full.textColor };
}

interface FcfsFallbackEntryRow {
  reactKey: string;
  title: string;
}

function mapFcfsFallbackEntryRows(
  entryList: ReadonlyArray<{ couponCd?: string | null }>,
  renderRow: (row: FcfsFallbackEntryRow) => React.ReactNode
): React.ReactNode[] {
  const seqByCd = new Map<string, number>();
  return entryList.map((entry, index) => {
    const couponCd = entry?.couponCd;
    const slot =
      couponCd != null && String(couponCd).trim() !== '' ? couponCd : '';
    const marker = slot === '' ? '__empty' : slot;
    const ordinal = (seqByCd.get(marker) ?? 0) + 1;
    seqByCd.set(marker, ordinal);

    return renderRow({
      reactKey: fcfsFallbackRowReactKey(slot, ordinal),
      title: fcfsFallbackEntryTitle(entryList.length, index),
    });
  });
}

/** NoticeBar 아이콘: 발급 예정(시각 안내)·대기 회차 노출 시 clock, 소진·종료·안내 노출 시 notice */
function fcfsCouponNoticeBarUiType(
  fcfsNoticeType: FcfsNoticeType | undefined,
  betweenRoundsEndedGapFlag: boolean
): 'clock' | 'notice' | undefined {
  if (fcfsNoticeType === undefined) return undefined;
  if (fcfsNoticeType === 'before' || fcfsNoticeType === 'preview') {
    return 'clock';
  }
  if (fcfsNoticeType === 'between') {
    return betweenRoundsEndedGapFlag ? 'notice' : 'clock';
  }
  return 'notice';
}

interface FcfsCouponModuleProps {
  promotionId: number;
  moduleId: number;
  bgType?: string;
  visualTokens: PromotionModuleVisualTokens;
  title?: string;
  subTitle?: string;
  promotionTitle?: string;
  module: PromotionFcfsCouponModuleType;
  noticeData?: PromotionNotice[];
  isDesktop: boolean;
  /** 행사 전·후 경계용 프로모션 전시 일정(있으면 FCFS 스케줄보다 우선). */
  promotionDisplayBounds?: FcfsPromotionDisplayBounds | null;
}

/** FCFS_COUPON · CouponSection2 + CouponItem2 */
function FcfsCouponModule({
  promotionId,
  moduleId,
  bgType,
  visualTokens,
  title,
  subTitle,
  promotionTitle,
  module,
  noticeData,
  isDesktop,
  promotionDisplayBounds,
}: FcfsCouponModuleProps) {
  const {
    displayRound,
    remainingByCd,
    showCheckingUi,
    phase,
    activeCountdownText,
    noticeBar,
    betweenRoundsEndedGap,
  } = useFcfsCouponModuleState({ module, moduleId, promotionDisplayBounds });

  const { presetBgType, customColorsFull } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'full',
  });

  const sectionColorType =
    customColorsFull != null
      ? sectionLiteFromFull(customColorsFull)
      : presetBgType;

  const itemSurface: CouponItem2ColorType =
    customColorsFull != null
      ? fcfsCouponItemColorTypeFromFull(customColorsFull)
      : mapPresetSectionToItemSurface(sectionColorType);

  const { sendGa4CouponClick, sendGa4ButtonClick } = useGa4Event();
  const { issue, isLoading: isIssuing } = useFcfsCouponIssue();

  const handleDownloadClick = useCallback(
    async (coupon: ICoupon, listIndex: number) => {
      if (isIssuing || phase !== 'ACTIVE') return;
      if (isCardSoldOut(coupon.couponCd, remainingByCd)) return;

      sendGa4CouponClick({
        area: 'promotion_limitedcoupon_download',
        page: 'promotion',
        issueCd: promotionId.toString(),
        issueNm: promotionTitle,
        listNo: listIndex + 1,
        couponCd: coupon.couponCd,
        couponNm: coupon.couponName,
      });
      await issue({ promotionId, moduleId, couponCd: coupon.couponCd });
    },
    [
      isIssuing,
      phase,
      remainingByCd,
      sendGa4CouponClick,
      promotionId,
      promotionTitle,
      issue,
      moduleId,
    ]
  );

  const onNoticeToggle = useCallback(
    (expanded: boolean) => {
      sendGa4ButtonClick({
        area: 'promotion_limitedcoupon_notice_button',
        page: 'promotion',
        issueCd: promotionId.toString(),
        issueNm: promotionTitle,
        text: `유의사항 ${expanded ? '보기' : '닫기'}`,
      });
    },
    [promotionId, promotionTitle, sendGa4ButtonClick]
  );

  const fcfsItemPhaseOptions = useMemo<FcfsCouponItemPhaseOptions | undefined>(
    () => (betweenRoundsEndedGap ? { betweenRoundsEndedGap: true } : undefined),
    [betweenRoundsEndedGap]
  );

  const itemStatusForFallback = fcfsCouponItemStatus(
    phase,
    fcfsItemPhaseOptions
  );

  const couponList = useMemo(() => {
    if (!displayRound) return [];

    const enriched = displayRound.round.couponItemList ?? [];
    if (enriched.length > 0) {
      if (showCheckingUi) {
        return [];
      }
      return enriched.map((coupon, index) => (
        <CouponItem2
          key={coupon.couponCd}
          title={coupon.couponName}
          desc={fcfsBoldNumericDescParts(
            resolveFcfsCouponItemDesc(
              phase,
              coupon.couponCd,
              coupon,
              remainingByCd,
              activeCountdownText,
              fcfsItemPhaseOptions
            )
          )}
          colorType={itemSurface}
          status={resolveFcfsCouponItemStatus(
            phase,
            coupon.couponCd,
            remainingByCd,
            fcfsItemPhaseOptions
          )}
          onClick={() => handleDownloadClick(coupon, index)}
        />
      ));
    }

    const entryList = displayRound.round.couponEntryList ?? [];
    if (entryList.length === 0) return [];
    if (showCheckingUi) {
      return [];
    }
    if (!phaseAllowsEntryFallback(phase)) return [];

    return mapFcfsFallbackEntryRows(entryList, row => (
      <CouponItem2
        key={row.reactKey}
        title={row.title}
        desc={fcfsBoldNumericDescParts(
          fcfsCouponDesc(phase, undefined, undefined, fcfsItemPhaseOptions)
        )}
        colorType={itemSurface}
        status={itemStatusForFallback}
      />
    ));
  }, [
    activeCountdownText,
    displayRound,
    handleDownloadClick,
    showCheckingUi,
    itemSurface,
    itemStatusForFallback,
    phase,
    remainingByCd,
    fcfsItemPhaseOptions,
  ]);

  const noticeListRows = useMemo(
    () =>
      noticeData
        ?.map(row => row.text.replace(/\n/g, '<br>').split('<br>'))
        .flat() ?? [],
    [noticeData]
  );

  const hasNoticeData = (noticeData?.length ?? 0) > 0;

  const noticeBarUiType = useMemo(
    () => fcfsCouponNoticeBarUiType(noticeBar?.type, betweenRoundsEndedGap),
    [betweenRoundsEndedGap, noticeBar?.type]
  );

  if (!isFcfsPublicDisplayPhase(phase)) {
    return null;
  }

  return (
    <CouponSection2
      title={title}
      subTitle={subTitle}
      colorType={sectionColorType}
      isLoaded={!showCheckingUi}
      noticeBarText={noticeBar?.text}
      noticeBarType={noticeBarUiType}
      couponList={couponList}
      isDesktop={isDesktop}
      noticeList={hasNoticeData ? noticeListRows : undefined}
      onNoticeToggle={hasNoticeData ? onNoticeToggle : undefined}
    />
  );
}

export default memo(FcfsCouponModule);
